import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Settings2, RotateCcw } from 'lucide-react';
import { DASHBOARD_SECTIONS, CHART_TYPE_OPTIONS, CHART_TYPE_SWITCHABLE } from '@/dashboards/dashboardSections';

/**
 * Modal de personalización del Dashboard (dashboard-personalizable).
 *
 * Lista NEGRA por diseño (CLAUDE.md §3 #9): el estado local arranca con las
 * prefs YA GUARDADAS del usuario (default vacío = todo visible/tipo original)
 * -- nunca con una lista blanca de "todo lo que existe hoy".
 *
 * `sections` viene de DASHBOARD_SECTIONS filtrado por lo que el usuario
 * realmente puede ver (ej. CallForce solo si su plan lo incluye) -- lo decide
 * el caller (DashboardPage), este componente no vuelve a evaluar capabilities.
 */
export default function DashboardSettings({ open, onOpenChange, sections }) {
  const { user, refreshUser } = useAuth();
  const [tabsHidden, setTabsHidden] = useState([]);
  const [chartsHidden, setChartsHidden] = useState({});
  const [chartTypes, setChartTypes] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const prefs = user?.dashboard_prefs || {};
    setTabsHidden(prefs.tabs_hidden || []);
    setChartsHidden(prefs.charts_hidden || {});
    setChartTypes(prefs.chart_types || {});
  }, [open, user?.dashboard_prefs]);

  const toggleTab = (key, checked) => {
    setTabsHidden((prev) => (checked ? prev.filter((k) => k !== key) : [...new Set([...prev, key])]));
  };

  const toggleChart = (tabKey, chartId, checked) => {
    setChartsHidden((prev) => {
      const current = prev[tabKey] || [];
      const next = checked ? current.filter((id) => id !== chartId) : [...new Set([...current, chartId])];
      return { ...prev, [tabKey]: next };
    });
  };

  const setChartType = (chartId, type) => {
    setChartTypes((prev) => {
      const next = { ...prev };
      if (!type || type === 'default') delete next[chartId];
      else next[chartId] = type;
      return next;
    });
  };

  const save = async (nextPrefs) => {
    setSaving(true);
    try {
      await api.patch('/auth/me', { dashboard_prefs: nextPrefs });
      await refreshUser();
      toast.success('Preferencias guardadas');
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron guardar las preferencias');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    // charts_hidden con arrays vacíos no aporta nada -- se limpia antes de guardar,
    // pero NO afecta el default (sigue siendo "todo visible" de cualquier forma).
    const cleanCharts = Object.fromEntries(
      Object.entries(chartsHidden).filter(([, ids]) => (ids || []).length > 0),
    );
    save({ tabs_hidden: tabsHidden, charts_hidden: cleanCharts, chart_types: chartTypes });
  };

  const handleReset = () => {
    setTabsHidden([]);
    setChartsHidden({});
    setChartTypes({});
    save({ tabs_hidden: [], charts_hidden: {}, chart_types: {} });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dashboard-settings-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-cyan-400" /> Personalizar Dashboard
          </DialogTitle>
          <DialogDescription>
            Elige qué cuadrantes y gráficos ver, y cómo. Se guarda en tu cuenta -- te acompaña a cualquier dispositivo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {sections.map((section) => {
            const tabVisible = !tabsHidden.includes(section.key);
            return (
              <div key={section.key} className="rounded-lg border border-white/10 p-3" data-testid={`dash-settings-section-${section.key}`}>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={tabVisible}
                    onCheckedChange={(c) => toggleTab(section.key, c)}
                    data-testid={`dash-settings-tab-${section.key}`}
                  />
                  <span className="text-sm font-semibold text-white">{section.label}</span>
                </label>

                {tabVisible && (
                  <div className="mt-2 ml-6 space-y-1.5">
                    {section.charts.map((chart) => {
                      const chartVisible = !(chartsHidden[section.key] || []).includes(chart.id);
                      const switchable = CHART_TYPE_SWITCHABLE.has(chart.id);
                      return (
                        <div key={chart.id} className="flex items-center justify-between gap-2" data-testid={`dash-settings-chart-row-${chart.id}`}>
                          <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                            <Checkbox
                              checked={chartVisible}
                              onCheckedChange={(c) => toggleChart(section.key, chart.id, c)}
                              data-testid={`dash-settings-chart-${chart.id}`}
                            />
                            <span className="text-xs text-muted-foreground truncate">{chart.label}</span>
                          </label>
                          {switchable && chartVisible && (
                            <Select
                              value={chartTypes[chart.id] || 'default'}
                              onValueChange={(v) => setChartType(chart.id, v)}
                            >
                              <SelectTrigger className="h-7 w-28 text-[10px] bg-black/20 border-white/10 shrink-0" data-testid={`dash-settings-type-${chart.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="default" className="text-xs">Original</SelectItem>
                                {CHART_TYPE_OPTIONS.map((o) => (
                                  <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleReset} disabled={saving} data-testid="dash-settings-reset">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <RotateCcw className="w-3.5 h-3.5 mr-2" />}
            Reset a defecto
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="dash-settings-save">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
